import { extend, isEqualString, unwrapBox, wrapBox } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { locate, Position } from "@dhx/ts-common/html";
import { KEY_CODES } from "@dhx/ts-common/keycodes";
import { View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents, Statuses, IDataEventsHandlersMap } from "@dhx/ts-data";
import { Layout } from "@dhx/ts-layout";
import { IListItem, List, ListEvents } from "@dhx/ts-list";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { KeyListener } from "./keyListener";
import locale from "./locales/en";

import { IHandlers } from "@dhx/ts-common/types";
import { emptyListView, selectAllView, unselectAllView } from "./helper";
import { ComboboxEvents, ComboState, ICombobox, IComboboxConfig, IComboboxEventHandlersMap, State } from "./types";

const template = item => {
	if (item.icon) {
		return `<span class="${item.icon} dhx_combobox-options__icon"></span> <span class="dhx_combobox-options__value">${item.value}</span>`;
	}
	if (item.src) {
		return `<img src="${item.src}" class="dhx_combobox-options__image"></img> <span class="dhx_combobox-options__value">${item.value}</span>`;
	}
	return `<span class="dhx_combobox-options__value">${item.value}</span>`;
};

export class Combobox extends View implements ICombobox {
	public data: DataCollection;
	public config: IComboboxConfig;
	public events: IEventSystem<DataEvents | ComboboxEvents, IComboboxEventHandlersMap | IDataEventsHandlersMap>;
	public list: List;
	public popup: Popup;

	protected _helper: Popup;

	private _keyListener: KeyListener;
	private _layout: Layout;

	private _state: {
		value: string;
		ignoreNext: boolean;
		canDelete: boolean;
		unselectActive: boolean;
		currentState: ComboState;
	};

	private _handlers: IHandlers;

	constructor(element: HTMLElement | string, config: IComboboxConfig) {
		super(element, extend({
			// selectAllButton: true
			template,
			listHeight: 224,
			cellHeight: 32,
		}, config));
		if (Array.isArray(this.config.data)) {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new DataCollection({}, this.events);
			this.data.parse(this.config.data);
		} else if (this.config.data) {
			this.data = this.config.data;
			this.events = this.data.events;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new DataCollection({}, this.events);
		}
		this.popup = new Popup();

		this.popup.events.on(PopupEvents.afterShow, () => {
			this.paint();
		});
		this.popup.events.on(PopupEvents.afterHide,() => {
			if (this.config.multiselection) {
				this._state.value = "";
			}
			this.paint();
		});
		this.popup.events.on(PopupEvents.beforeHide, (fromOuterClick) => {
			if (fromOuterClick) {
				this.events.fire(ComboboxEvents.close);
			}
		});

		if (this.config.readonly) {
			this._keyListener = new KeyListener();
		}

		this._state = {
			value: "",
			ignoreNext: false,
			canDelete: false,
			unselectActive: false,
			currentState: ComboState.default
		};

		this._initHandlers();
		this._createLayout();
		this._initEvents();

		const vnode = create({
			render: () => this._draw(),
			hooks: {
				didRedraw: () => {
					if (this.popup.isVisible()) {
						this.focus();
						this._configurePopup();
					}
				}
			}
		});
		// const container = toNode(element);
		this.mount(element, vnode);
	}
	setState(state: State) {
		switch (state) {
			case "success":
				this._state.currentState = ComboState.success;
				break;
			case "error":
				this._state.currentState = ComboState.error;
				break;
			default:
				this._state.currentState = ComboState.default;
				break;
		}
		this.paint();
	}
	focus() {
		if (this.config.disabled) {
			return false;
		}
		const rootView = this.getRootView();
		rootView.refs.input.el.focus();
	}
	enable() {
		this.config.disabled = false;
		this.paint();
	}
	disable() {
		this.config.disabled = true;
		this.paint();
	}
	clear() {
		if (this.config.disabled) {
			return false;
		}
		this.list.selection.remove();
		this._state.value = "";
		this._filter();
		this.paint();
	}
	getValue<T extends boolean = false>(asArray?: T): T extends true ? string[] : string {
		const ids = this.list.selection.getId();
		if (asArray) {
			return wrapBox(ids) as any;
		}
		return Array.isArray(ids) ? ids.join(",") : ids as any;
	}
	setValue(ids: string[] | string) {
		if (this.config.disabled) {
			return false;
		}
		this._filter();
		this.list.selection.remove();
		this._state.value = "";
		if (this.config.multiselection) {
			if (typeof ids === "string") {
				ids = ids.split(",");
			}
			ids.forEach(id => this.list.selection.add(id));
		} else {
			const id = unwrapBox(ids);
			this.list.selection.add(id);
			const item = this.data.getItem(id);
			if (item) {
				this._state.value = this._getItemText(item);
			}
		}
		this.paint();
	}
	destructor() {
		this.popup.destructor();
		this.events.clear();
		this.list.destructor();
		this._layout.config = null;
		this._layout.destructor();
		this.unmount();
	}

	private _createLayout() {
		const list = this.list = new List(null, {
			template: this.config.template,
			virtual: this.config.virtual,
			keyNavigation: () => this.popup.isVisible(),
			multiselection: this.config.multiselection,
			itemHeight: this.config.cellHeight,
			height: this.config.listHeight,
			data: this.data
		});
		const layout = this._layout = new Layout(this.popup.getContainer(), {
			css: "dhx_combobox-options dhx_combobox__options",
			rows: [
				{
					id: "select-unselect-all",
					hidden: !this.config.multiselection || !this.config.selectAllButton
				},
				{ id: "list", css: "dhx_layout-cell--gravity" },
				{
					id: "not-found",
					hidden: true,
				}
			],
			on: {
				click: {
					".dhx_combobox__action-select-all": this._handlers.selectAll
				}
			}
		});

		layout.getCell("list").attach(list);
		if (this.config.multiselection && this.config.selectAllButton) {
			layout.getCell("select-unselect-all").attach(selectAllView);
		}
	}
	private _initHandlers() {
		if (this.config.help) {
			this._helper = new Popup({css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light"});
			this._helper.attachHTML(this.config.help);
		}
		this._handlers = {
			showHelper: e => {
				e.preventDefault();
				e.stopPropagation();
				this._helper.show(e.target);
			},
			selectAll: () => {
				this.list.selection.remove();
				if (this._state.unselectActive) {
					this.data.filter();
					(this.list.selection.getId() as string[]).forEach(id => {
						this.list.selection.remove(id);
					});
					if (this.config.selectAllButton) {
						this._layout.getCell("select-unselect-all").attach(selectAllView);
						this._state.unselectActive = false;
					}
				} else {
					this.data.filter();
					this.list.selection.add();
					if (this.config.selectAllButton) {
						this._layout.getCell("select-unselect-all").attach(unselectAllView);
						this._state.unselectActive = true;
					}
				}
				this.paint();
			},
			onkeydown: (e: KeyboardEvent) => {
				if (!this.popup.isVisible() && e.which === KEY_CODES.DOWN_ARROW) {
					this._showOptions();
				}
				if (this.popup.isVisible() && e.which === KEY_CODES.RIGHT_ARROW) {
					if (this.config.readonly && !this.config.multiselection) {
						const focused = this.list.getFocusIndex();
						if (focused >= 0 && focused <= this.data.getLength()) {
							e.preventDefault();
							this.list.setFocusIndex(focused + 1);
						}
					}
				}
				if (this.popup.isVisible() && e.which === KEY_CODES.LEFT_ARROW) {
					if (this.config.readonly && !this.config.multiselection) {
						const focused = this.list.getFocusIndex();
						if (focused >= 0 && focused <= this.data.getLength()) {
							e.preventDefault();
							this.list.setFocusIndex(focused - 1);
						}
					}
				}
				if (this.popup.isVisible() && e.which === KEY_CODES.ESC) {
					this._hideOptions();
				}
				if (this.popup.isVisible() && e.which === KEY_CODES.ENTER) {
					this.setValue(this.list.data.getId(this.list.getFocusIndex()));
				}
			},
			onkeyup: (e: KeyboardEvent) => {
				if (!this.config.multiselection || this.config.showItemsCount) {
					return;
				}
				if (this._state.ignoreNext) {
					this._state.ignoreNext = false;
					return;
				}
				if (e.which === KEY_CODES.BACKSPACE && this._state.canDelete && (this.list.selection.getId() as string[]).length) {
					const selected = this.list.selection.getId() as string[];
					const id = selected[selected.length - 1];
					this.list.selection.remove(id);

					this.paint();
					this.paint();
				}
			},
			oninput: (e: Event) => {
				if (this.config.disabled) {
					return;
				}
				const input = e.target as HTMLInputElement;
				const value = input.value;
				this.events.fire(ComboboxEvents.input, [value]);

				this._state.value = value;
				this._filter();

				if (!value.length) {
					this._state.ignoreNext = true;
					this._state.canDelete = true;
				} else {
					this._state.canDelete = false;
				}

				if (!this.config.multiselection) {
					this.list.selection.remove();
					this.paint();
				}

				if (!this.popup.isVisible()) {
					this._showOptions();
				}
			},
			oninputclick: (e: MouseEvent) => {
				if (this.config.disabled) {
					return;
				}
				this.focus();
				if ((e.target as HTMLElement).classList.contains("dhx_combobox__action-remove")) {
					const id = locate(e);
					if (!id) {
						return;
					}
					this.list.selection.remove(id);
					this.paint();
					return;
				}
				if ((e.target as HTMLElement).classList.contains("dhx_combobox__action-clear-all")) {
					(this.list.selection.getId() as string[]).forEach(id => this.list.selection.remove(id));
					if (this.config.selectAllButton && this._state.unselectActive) {
						this._layout.getCell("select-unselect-all").attach(selectAllView);
						this._state.unselectActive = false;
					}
					this.paint();
					return;
				}
				e.preventDefault();
				if (!this.popup.isVisible()) {
					this._showOptions();
					return;
				}
				this.focus();
			},
			toggleIcon: () => {
				this.focus();
				if (this.popup.isVisible()) {
					this._hideOptions();
				} else {
					this._showOptions();
				}
			}
		};
	}
	private _initEvents() {
		this.list.events.on(ListEvents.click, () => {
			if (!this.config.multiselection) {
				this._hideOptions();
			}
		});

		this.data.events.on(DataEvents.change, (id: string, status: Statuses, item: IListItem) => {
			if (item && item.hasOwnProperty("$selected")) {
				this._updateSelectedItem(id);
			}
		});

		if (this.config.readonly) {
			this.popup.events.on(PopupEvents.afterShow, () => {
				if (this._state.value) {
					const id = this.list.selection.getId();
					this.list.setFocus(id as string);
				} else {
					this.list.setFocus(this.data.getId(0));
				}
				this._keyListener.startNewListen((val) => this._findBest(val));
			});
		}
	}
	private _showOptions() {
		if (this._state.value.length) {
			this._state.canDelete = true;
		}
		this._filter();
		if (this._configurePopup()) {
			this.events.fire(ComboboxEvents.open);
		}
	}
	private _configurePopup() {
		const rootView = this.getRootView();
		if (!rootView || !rootView.refs || !rootView.refs.holder) {
			return false;
		}
		if (!this.popup.isVisible()) {
			const holderNode: HTMLElement = rootView.refs.holder.el;
			this.popup.getContainer().style.width = holderNode.offsetWidth + "px";
			this.popup.show(holderNode, { mode: Position.bottom });
		}
		return true;
	}
	private _hideOptions() {
		if (this.config.readonly) {
			this._keyListener.endListen();
		}
		this.list.setFocus(this.data.getId(0));
		if (!this.config.multiselection && !this.config.readonly && !this.list.selection.contains()) {
			this._state.value = "";
		}

		this.popup.hide();
		this.paint();

		this.events.fire(ComboboxEvents.close);
	}
	private _filter() {
		if (this.config.readonly) {
			return;
		}
		this.data.filter(item => this.config.filter
			? this.config.filter(item, this._state.value)
			: isEqualString(this._state.value, this._getItemText(item))
		);
		if (this.config.multiselection) {
			this.list.setFocus(this.data.getId(0));
		} else {
			const index = this.data.getIndex(this.list.selection.getId() as string);
			this.list.setFocus(this.data.getId(index > -1 ? index : 0));
		}
		if (this.data.getLength() === 0) {
			if (this.config.multiselection && this.config.selectAllButton) {
				this._layout.getCell("select-unselect-all").hide();
			}
			this._layout.getCell("list").hide();
			this._layout.getCell("not-found").attach(emptyListView);
			this._layout.getCell("not-found").show();
		} else {
			if (this.config.multiselection && this.config.selectAllButton) {
				this._layout.getCell("select-unselect-all").show();
			}
			if (this._layout.getCell("not-found").isVisible()) {
				this._layout.getCell("list").show();
				this._layout.getCell("not-found").hide();
			}
		}
	}
	private _findBest(value: string) {
		const best = this.data.find(item => isEqualString(value, this._getItemText(item)));
		if (!best) {
			return;
		}

		if (this.list.selection.getId() === best.id) {
			return;
		}

		this.list.setFocus(best.id);
		this.list.selection.add(best.id);
		this.paint();
	}
	private _draw() {
		const item = this.config.multiselection ? null : this.data.getItem(this.list.selection.getId() as string);
		const showPlaceholder = !this.list.selection.getId() || this.list.selection.getId().length === 0;
		const width = this.config.labelInline && this.config.labelWidth ? this.config.labelWidth : "";
		const { required } = this.config;
		return el(".dhx_widget.dhx_combobox" +
			(this.config.labelInline ? ".dhx_combobox--label-inline" : "") +
			(this.config.hiddenLabel ? ".dhx_combobox--sr_only" : "") +
			(this.config.required ? ".dhx_combobox--required" : "") +
			(this.config.css ? "." + this.config.css : ""), {
				dhx_widget_id: this._uid,
				onkeydown: this._handlers.onkeydown,
				onkeyup: this._handlers.onkeyup
			}, [
				this.config.label ? el("label.dhx_label.dhx_combobox__label", {
					style: {minWidth: width, maxWidth: width},
					class: this.config.help ? "dhx_label--with-help" : "",
					onclick: this._handlers.oninputclick
				}, this.config.help ? [
					el("span.dhx_label__holder", this.config.label),
					el("span.dhx_label-help.dxi.dxi-help-circle-outline", {
						tabindex: "0",
						role: "button",
						onclick: this._handlers.showHelper
					}),
				] : this.config.label) : null,
				el("div.dhx_combobox-input-box" +
					// (this.popup.isVisible() ? ".dhx_combobox-input-box" : "") +
					(this.config.disabled ? ".dhx_combobox-input-box--disabled" : "") +
					(this.config.readonly ? ".dhx_combobox-input-box--readonly" : "") +
					(this._state.currentState === ComboState.error ? ".dhx_combobox-input-box--state_error" : "") +
					(this._state.currentState === ComboState.success ? ".dhx_combobox-input-box--state_success" : ""), {
						_ref: "holder"
					}, [
						el("div.dhx_combobox-input__icon", {
							onclick: this._handlers.toggleIcon
						}, [
								el("span" + (this.popup.isVisible() ? ".dxi.dxi-menu-up" : ".dxi.dxi-menu-down"))
							]),
						el("div.dhx_combobox-input-list-wrapper", {
							onclick: this._handlers.oninputclick
						},[
							el("ul.dhx_combobox-input-list", [
									...this._drawSelectedItems(), // selected items for multiselection mode
									el("li.dhx_combobox-input-list__item.dhx_combobox-input-list__item--input", [
										el("input.dhx_combobox-input", {
											oninput: this._handlers.oninput,
											_ref: "input",
											_key: this._uid,
											type: "text",
											placeHolder: showPlaceholder && this.config.placeholder ? this.config.placeholder : undefined,
											value: this.config.readonly && item ? this._getItemText(item) : this._state.value,
											readOnly: this.config.readonly || this.config.disabled,
											required
										})
									])
								]
							)
						]),
					])
			]);
	}
	private _drawSelectedItems() {
		if (!this.config.multiselection) {
			return [];
		}
		if (this.config.showItemsCount) {
			const count = this.list.selection.getId().length;
			return count ? [
				el("li.dhx_combobox-input-list__item.dhx_combobox-tag", [
					el("span.dhx_combobox-tag__value", itemsCountTemplate(count, this.config.showItemsCount)),
					el("button.dhx_button.dhx_combobox-tag__action.dhx_combobox__action-clear-all", [
						el("span.dhx_button__icon.dxi.dxi-close-circle")
					])
				])
			] : [];
		}
		return (this.list.selection.getId() as string[]).map(id => {
			const item = this.data.getItem(id);
			if (!item) {
				return null;
			}
			return el("li.dhx_combobox-input-list__item.dhx_combobox-tag", { dhx_id: id }, [
				this._drawImageOrIcon(item),
				el("span.dhx_combobox-tag__value", this._getItemText(item)),
				el("button.dhx_button.dhx_button--icon.dhx_combobox-tag__action.dhx_combobox__action-remove", {
					type:"button"
				},[
					el("span.dhx_button__icon.dxi.dxi-close-circle")
				])
			]);
		});
	}
	private _drawImageOrIcon(item: any) {
		if (item.src) {
			return el("img.dhx_combobox-tag__image", { src: item.src });
		} else if (item.icon) {
			return el("span.dhx_combobox-tag__icon", { class: item.icon });
		}
		return null;
	}
	private _getItemText(item: any) {
		if (!item) {
			return null;
		}
		return item.value;
	}
	private _updateSelectedItem(id: string) {
		if (this.config.multiselection) {
			const selected = this.data.getItem(id).$selected;
			if (selected) {
				if (this.config.selectAllButton && !this._state.unselectActive && this.data.getLength() === (this.list.selection.getId() as string[]).length) {
					this._layout.getCell("select-unselect-all").attach(unselectAllView);
					this._state.unselectActive = true;
				}
			} else {
				if (this.config.selectAllButton && this._state.unselectActive) {
					this._layout.getCell("select-unselect-all").attach(selectAllView);
					this._state.unselectActive = false;
				}
			}

			if (!this._state.value.length) {
				this._state.canDelete = true;
			}

			this.paint();
			return;
		}
		this._state.value = this._getItemText(this.data.getItem(id)) || "";
		this.paint();
	}

}

type TemplateFn = (count: number) => string;
function itemsCountTemplate(count, templateFN: boolean | TemplateFn) {
	if (typeof templateFN === "function") {
		return templateFN(count);
	} else {
		return count + " " + locale.selectedItems;
	}
}